import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadFlantListStatus: 'idle',
    loadFlantListError: undefined as any,
    loadFlantListLastArgs: undefined as any,
    loadFlantStatus: 'idle',
    loadFlantError: undefined as any,
    saveFlantStatus: 'idle',
    saveFlantError: undefined as any,
    deleteFlantStatus: 'idle',
    deleteFlantError: undefined as any,

    flantList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadFlantList = createAsyncThunk('flantSlice/loadFlantList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().flantSlice.loadFlantListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/flant/?obj=${parentModel.OBJNR}&TYP=0&FLNO=${parentModel.FLSTMNR}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadFlantListStatus } = getState().flantSlice
        return loadFlantListStatus !== 'pending'
    }
})

export const loadFlant = createAsyncThunk('flantSlice/loadFlant', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/flant/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadFlantStatus } = getState().flantSlice
        return loadFlantStatus !== 'pending'
    }
})

export const saveFlant = createAsyncThunk('flantSlice/saveFlant', async ({ flant }: any, { rejectWithValue }: any) => {
    let promises = []
    if (flant?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/flant/${ flant?.HDR_ID }/`,
            prepareData(flant),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/flant/`,
            prepareData(flant),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveFlantStatus } = getState().flantSlice
        return saveFlantStatus !== 'pending'
    }
})

export const deleteFlant = createAsyncThunk('flantSlice/deleteFlant', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/flant/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteFlantStatus } = getState().flantSlice
        return deleteFlantStatus !== 'pending'
    }
})

const FlantSlice = createSlice({
    name: 'flantSlice',
    initialState,
    reducers: {
        resetloadFlantListError: (state) => {
            state.loadFlantListError = undefined
        },
        resetloadFlant: (state) => {
            state.formItem = undefined
            state.loadFlantError = undefined
        },
        resetloadFlantError: (state) => {
            state.loadFlantError = undefined
        },
        resetsaveFlant: (state) => {
            state.formItem = undefined
            state.saveFlantError = undefined
        },
        resetsaveFlantError: (state) => {
            state.saveFlantError = undefined
        },
        resetdeleteFlantError: (state) => {
            state.deleteFlantError = undefined
        },
        removeFromFlantListById: (state, action) => {
            state.flantList = state.flantList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromFlantListByIds: (state, action) => {
            state.flantList = state.flantList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.flantList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadFlantListStatus = 'preloaded'
            state.loadFlantListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.flantList = []
            state.loadFlantListStatus = 'idle'
            state.loadFlantListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadFlantList.pending, (state) => {
            state.loadFlantListStatus = 'pending'
            state.loadFlantListError = undefined
        })
        builder.addCase(loadFlantList.fulfilled, (state, action) => {
            state.loadFlantListStatus = 'idle'

            if (!action.payload) { return }

            state.loadFlantListLastArgs = action.payload.args

            {
                state.flantList = action.payload.data

            }
        })
        builder.addCase(loadFlantList.rejected, (state, action) => {
            state.loadFlantListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadFlantListError = action.error.message
            } else {
                state.loadFlantListError = action.payload
            }
        })

        builder.addCase(loadFlant.pending, (state) => {
            state.loadFlantStatus = 'pending'
            state.loadFlantError = undefined
        })
        builder.addCase(loadFlant.fulfilled, (state, action) => {
            state.loadFlantStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.flantList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.flantList.push(newItem) }
                else { state.flantList[i] = { ...state.flantList[i], ...newItem } }
            }
        })
        builder.addCase(loadFlant.rejected, (state, action) => {
            state.loadFlantStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadFlantError = action.error.message
            } else {
                state.loadFlantError = action.payload
            }
        })

        builder.addCase(saveFlant.pending, (state) => {
            state.saveFlantStatus = 'pending'
            state.saveFlantError = undefined
        })
        builder.addCase(saveFlant.fulfilled, (state, action) => {
            state.saveFlantStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.flantList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.flantList.push(newItem) }
                else { state.flantList[i] = { ...state.flantList[i], ...newItem } }
            }
        })
        builder.addCase(saveFlant.rejected, (state, action) => {
            state.saveFlantStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveFlantError = action.error.message
            } else {
                state.saveFlantError = action.payload
            }
        })

        builder.addCase(deleteFlant.pending, (state) => {
            state.deleteFlantStatus = 'pending'
            state.deleteFlantError = undefined
        })
        builder.addCase(deleteFlant.fulfilled, (state, action) => {
            state.deleteFlantStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.flantList = state.flantList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.flantList = state.flantList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteFlant.rejected, (state, action) => {
            state.deleteFlantStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteFlantError = action.error.message
            } else {
                state.deleteFlantError = action.payload
            }
        })

    }
})

export const {
    resetloadFlantListError,
    resetloadFlant,
    resetloadFlantError,
    resetsaveFlant,
    resetsaveFlantError,
    resetdeleteFlantError,
    removeFromFlantListById,
    removeFromFlantListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = FlantSlice.actions
export default FlantSlice.reducer
